import { get, patch } from "@app/common/api";
import {
  ApiTranscriptResponse,
  MeetingReport,
  SessionInfo,
  VideoRecording,
  UnauthorizedVideoRecording,
  Transcript,
} from "@app/common/types";
import { isResponseErrorWithStatus } from "@app/common/utils";
import { parseUTCDateTimeFromString } from "@app/utils/utils";

import {
  getMeetingReportFromResponse,
  parseKeywords,
  parseSpeakers,
  parseTranscriptSummary,
  parseTranscriptTurns,
} from "./models";
import { MeetingFeedback } from "../types";

export const getMeetingReport = async (
  sessionId: string
): Promise<MeetingReport | undefined> => {
  try {
    const reportRes = await get(`/sessions/${sessionId}/metrics/post-call`, {
      withCreds: true,
    });
    return getMeetingReportFromResponse(reportRes, sessionId);
  } catch (error: any) {
    if (isResponseErrorWithStatus(error, 463)) {
      return undefined;
    }

    if (isResponseErrorWithStatus(error, 403)) {
      return {
        meetingId: sessionId,
        insufficientData: true,
        inaccessible: true,
      };
    }

    throw error;
  }
};

export const getMeetingInfo = async (sessionId: string) => {
  const res: SessionInfo = await get(`/sessions/${sessionId}`, {
    withCreds: true,
  });
  return res;
};

export const getMeetingFeedback = async (sessionId: string) => {
  return get(`/sessions/${sessionId}/feedback`, { withCreds: true });
};

export const getMyFeedback = async (sessionId: string) => {
  return get(`/sessions/${sessionId}/feedback/me`, { withCreds: true });
};

export const submitFeedback = async (
  sessionId: string,
  feedback: MeetingFeedback
): Promise<void> => {
  return patch(`/sessions/${sessionId}/feedback/me`, feedback, {
    withCreds: true,
  });
};

export const getTranscript = async (sessionId: string): Promise<Transcript> => {
  const res: ApiTranscriptResponse = await get(
    `/sessions/${sessionId}/transcript`,
    { withCreds: true }
  );

  return {
    keywords: parseKeywords(res?.data?.sessionTranscript?.topics),
    summary: parseTranscriptSummary(res?.data?.sessionTranscript),
    turns: parseTranscriptTurns(res?.data?.sessionTranscript?.turns),
    isEdited: res?.data?.sessionTranscript?.isEdited ?? false,
    speakers: parseSpeakers(res?.data?.sessionTranscript),
  };
};

export const getRecordingInfo = async (
  sessionId: string
): Promise<VideoRecording | UnauthorizedVideoRecording> => {
  const recordingInfo = await get(`/sessions/${sessionId}/recordings`, {
    withCreds: true,
  }).catch((error: unknown) => {
    if (isResponseErrorWithStatus(error, 403)) {
      return {
        playlistUrl: null,
        status: "unauthorized",
      };
    }
    throw error;
  });

  if (recordingInfo.status === "unauthorized") {
    return recordingInfo;
  }

  const startTime = recordingInfo.startTime
    ? parseUTCDateTimeFromString(recordingInfo.startTime)
    : null;
  const endTime = recordingInfo.endTime
    ? parseUTCDateTimeFromString(recordingInfo.endTime)
    : null;

  return { ...recordingInfo, startTime, endTime };
};
